import {
  DocumentPaymentMethodEnum,
  PrintedDocumentFragment,
} from "@/__generated__/graphql";
import { identifierAndTypenameToId } from "@/objects/@utils";
import { Utils } from "@/utils";
import { type InvoiceTemplate } from "./DocumentPreview.content";

/**
 * A line as it is printed from: what both ends read of it
 * (``PrintedLineItem``), the rates of its tax where its business reads them,
 * and - while its quantity is typed on the canvas - the price it was typed
 * against.
 */
type LineToPrint = {
  description?: string | null;
  quantity?: number | string | null;
  amount?: number | string | null;
  amountSubtotal?: number | string | null;
  amountTax?: number | string | null;
  unitPrice?: number | null;
  tax?: {
    name?: string | null;
    totalRate?: number | string | null;
    rates?: ({ rate?: number | string | null } | null)[] | null;
  } | null;
};

/** A line as the invoice prints it, every figure in words already. */
export type PrintedLine = {
  description: string;
  quantity: string;
  unitPrice: string;
  tax: string;
  total: string;
};

/**
 * Everything an invoice prints, worded once. The canvas its business edits it
 * on and the PDF its payer reads and downloads are both drawn from this, so
 * neither says anything the other does not: what differs between them is
 * where the canvas takes an edit, and how each is drawn.
 */
export type InvoiceDescription = {
  number: string;
  issued: string;
  billedTo: string[];
  issuedBy: string[];
  headline: string;
  notes: string | null;
  lines: PrintedLine[];
  totals: { label: string; value: string }[];
  eTransfer: {
    instructions: string;
    isGiven: boolean;
    reference: string | null;
  } | null;
  card: { url: string } | null;
  footer: string | null;
};

/**
 * The invoice *document* as it prints, read alike from its business's record
 * and from the page it is shared at - each asks for what it prints in the same
 * fragments (``PrintedDocument``, ``PrintedLineItem``) - with the *lines* it
 * has, the *template* it prints with, and whether the page it is paid at takes
 * a card: that page, the one the reader is on (*publicUrl*), else the one its
 * identifier is read at.
 */
export function describeInvoice({
  document,
  lines,
  template,
  canAcceptPayments,
  publicUrl,
}: {
  document: PrintedDocumentFragment & {
    identifier?: string | null;
    contact?: {
      name?: string | null;
      emailAddress?: string | null;
      organization?: { name?: string | null } | null;
      organizationName?: string | null;
    } | null;
    entity?: { name?: string | null } | null;
  };
  lines: LineToPrint[];
  template: InvoiceTemplate | null | undefined;
  canAcceptPayments: boolean;
  publicUrl?: string;
}): InvoiceDescription {
  // Calendar days, kept as their midnight in UTC: read in the reader's own
  // zone, the first of the month was the last of the one before west of it.
  const day = (value: string | null | undefined) =>
    value
      ? new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          timeZone: "UTC",
        }).format(new Date(value))
      : null;
  // A figure as the canvas's own cells show one: its sign and its cents.
  const money = (value: number) => Utils.fCurrency(value, false, true);
  const currency = document.amountCurrency?.toUpperCase();
  const owed = (value: number) =>
    [money(value), currency].filter(Boolean).join(" ");
  const addressLines = (address: string | null | undefined) =>
    (address ?? "").split("\n").filter((line) => line.trim());

  const printed = lines.map((line) => {
    const subtotal = Number(line.amountSubtotal ?? line.amount ?? 0);
    const quantity = Number(line.quantity ?? 1);
    const tax = Number(line.amountTax ?? 0);
    const rates = (line.tax?.rates ?? []).filter(Boolean);
    const rate = rates.length
      ? rates.reduce((sum, part) => sum + Number(part?.rate ?? 0), 0)
      : Number(line.tax?.totalRate ?? NaN);
    return {
      subtotal,
      tax,
      // Each figure as the canvas's cell for it holds it: how many as said,
      // no tax as nothing.
      line: {
        description: line.description || "-",
        quantity: String(line.quantity ?? 1),
        unitPrice: money(
          line.unitPrice ?? (quantity > 0 ? subtotal / quantity : subtotal),
        ),
        tax: !line.tax?.name
          ? ""
          : Number.isFinite(rate)
            ? `${line.tax.name} (${rate}%)`
            : line.tax.name,
        total: money(subtotal + tax),
      },
    };
  });
  const subtotal = printed.reduce((sum, line) => sum + line.subtotal, 0);
  const tax = printed.reduce((sum, line) => sum + line.tax, 0);
  // What is owed is the server's, less what is paid of it; a record not yet
  // saved has none, and owes what its lines come to.
  const due =
    document.amountDue != null
      ? Number(document.amountDue)
      : subtotal + tax - Number(document.amountDiscounted ?? 0);
  const dueBy = day(document.dateDue);
  // Whom it is billed to: the address it is billed to, else its contact - its
  // company, as the contact is known by, or how it is reached.
  const contact = document.contact;
  const company =
    contact?.organizationName ?? contact?.organization?.name ?? null;
  const billedTo = addressLines(document.payableTo);
  const issuedBy = addressLines(document.payableFrom);
  const reference = document.reference || document.name || null;
  const methods = document.paymentMethods ?? [];
  const paymentPageUrl =
    publicUrl ||
    (document.identifier
      ? `${window.location.origin}/public/${identifierAndTypenameToId(
          document.identifier,
          "PublicDocumentObject",
        )}`
      : null);

  return {
    number: reference ?? "-",
    issued: day(document.dateIssued) ?? "-",
    billedTo: billedTo.length
      ? billedTo
      : [company || contact?.name || contact?.emailAddress || "-"],
    issuedBy: issuedBy.length ? issuedBy : [document.entity?.name || "-"],
    headline: `${owed(due)} due ${dueBy ? `by ${dueBy}` : "on receipt"}`,
    notes: document.description || null,
    lines: printed.map(({ line }) => line),
    totals: [
      { label: "Total excluding tax", value: money(subtotal) },
      { label: "Total tax", value: money(tax) },
      { label: "Amount due", value: owed(due) },
    ],
    eTransfer: methods.includes(DocumentPaymentMethodEnum.ETransfer)
      ? {
          instructions:
            template?.paymentInstructions ||
            `No e-transfer details on this invoice: ask ${document.entity?.name} for them.`,
          isGiven: Boolean(template?.paymentInstructions),
          reference: reference
            ? `Include ${reference} with your payment.`
            : null,
        }
      : null,
    card: canAcceptPayments && paymentPageUrl ? { url: paymentPageUrl } : null,
    footer: template?.footer || null,
  };
}
